import { ToastQueue, useToastQueue } from '@react-stately/toast'
import React, { useCallback, useMemo } from 'react'
import { useToastRegion } from 'react-aria'

import { type ToastContent, ToastContext, type ToastContextValue } from '@/lib/provider/ToastContext'
import { Toast } from '@components/toast/Toast'

const toastQueue = new ToastQueue<ToastContent>({
  maxVisibleToasts: 5,
})

export type ToastPosition = 'top-left' | 'top-center' | 'top-right' | 'bottom-left' | 'bottom-center' | 'bottom-right'

interface ToastProviderProps {
  children: React.ReactNode
  position?: ToastPosition
}

export const ToastProvider: React.FC<ToastProviderProps> = ({
  children,
  position = 'top-right',
}) => {
  const showToast = useCallback((content: ToastContent) => {
    // If an id is provided, close any existing toast with same id (dedupe/replace behavior)
    if (content.id) {
      const existing = toastQueue.visibleToasts.find(t => t.content.id === content.id)
      if (existing) {
        toastQueue.close(existing.key)
      }
    }
    return toastQueue.add(
      {
        ...content,
        variant: content.variant ?? 'info',
      },
      {
        onClose: content.onClose,
      }
    )
  }, [])

  const closeToast = useCallback((key: string) => {
    toastQueue.close(key)
  }, [])

  const closeAllToasts = useCallback(() => {
    toastQueue.visibleToasts.forEach(toast => {
      toastQueue.close(toast.key)
    })
  }, [])

  const contextValue = useMemo<ToastContextValue>(
    () => ({
      showToast,
      closeToast,
      closeAllToasts,
    }),
    [showToast, closeToast, closeAllToasts]
  )

  return (
    <ToastContext.Provider value={contextValue}>
      {children}
      <ToastRegion position={position} />
    </ToastContext.Provider>
  )
}

interface ToastRegionProps {
  position: ToastPosition
}

const ToastRegion: React.FC<ToastRegionProps> = ({ position }) => {
  const state = useToastQueue(toastQueue)
  const ref = React.useRef(null)

  const { regionProps } = useToastRegion({ 'aria-label': 'Notifications' }, state, ref)

  // Position classes mapping
  const positionClasses: Record<ToastPosition, string> = {
    'top-left': 'top-4 left-4',
    'top-center': 'top-4 left-1/2 -translate-x-1/2',
    'top-right': 'top-4 right-4',
    'bottom-left': 'bottom-4 left-4',
    'bottom-center': 'bottom-4 left-1/2 -translate-x-1/2',
    'bottom-right': 'bottom-4 right-4',
  }

  // For top positions, we want newest toasts at the top (reverse order)
  const isTopPosition = position.startsWith('top')
  const toasts = isTopPosition
    ? [...state.visibleToasts].reverse()
    : state.visibleToasts

  if (state.visibleToasts.length === 0) {
    return null
  }

  return (
    <div
      {...regionProps}
      ref={ref}
      className={`
        fixed
        ${positionClasses[position]}
        pointer-events-none z-9999 flex w-full max-w-sm flex-col gap-2
      `}
      style={{
        maxHeight: 'calc(100vh - 2rem)',
      }}
    >
      {toasts.map(toast => (
        <Toast
          key={toast.key}
          toast={toast}
          state={state}
          position={position}
        />
      ))}
    </div>
  )
}
